import React, { useState, useEffect } from 'react';
import { Layout, Menu, Button, Switch, Dropdown, Avatar, Drawer } from 'antd';
import {
  DashboardOutlined,
  DesktopOutlined,
  MonitorOutlined,
  HeartOutlined,
  BellOutlined,
  SettingOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  LogoutOutlined,
  CodeOutlined,
  ClusterOutlined,
  DatabaseOutlined,
  HistoryOutlined,
  DeploymentUnitOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { setDisplaySettings } from '../../store/slices/settingsSlice';
import { logout } from '../../store/slices/authSlice';

const { Header, Sider, Content } = Layout;

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { theme, autoRefresh } = useSelector((state: RootState) => state.settings);
  const { user } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    const checkIsMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        setCollapsed(true);
      }
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  const menuItems = [
    {
      key: '/',
      icon: <DashboardOutlined />,
      label: '系统概览',
    },
    {
      key: '/gpu-management',
      icon: <DesktopOutlined />,
      label: 'GPU管理',
    },
    {
      key: '/task-version-management',
      icon: <CodeOutlined />,
      label: '任务版本管理',
    },
    {
      key: '/task-metadata-management',
      icon: <DatabaseOutlined />,
      label: '任务元数据管理',
    },
    {
      key: '/task-workflow-config-management',
      icon: <DeploymentUnitOutlined />,
      label: '任务Workflow配置',
    },
    {
      key: '/gpu-cluster-monitoring',
      icon: <ClusterOutlined />,
      label: 'GPU集群监控',
    },
    {
      key: '/task-monitoring',
      icon: <MonitorOutlined />,
      label: '任务监控',
    },
    {
      key: '/recent-processed-tasks',
      icon: <HistoryOutlined />,
      label: '最近处理任务',
    },
    {
      key: '/task-test-template',
      icon: <ExperimentOutlined />,
      label: '任务测试模版',
    },
    {
      key: '/health-check',
      icon: <HeartOutlined />,
      label: '健康检查',
    },
    {
      key: '/alert-management',
      icon: <BellOutlined />,
      label: '告警管理',
    },
    {
      key: '/settings',
      icon: <SettingOutlined />,
      label: '系统设置',
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
    if (isMobile) {
      setMobileMenuVisible(false);
    }
  };

  const handleThemeChange = (checked: boolean) => {
    dispatch(
      setDisplaySettings({
        theme: checked ? 'dark' : 'light',
        refreshInterval: 30000,
        language: 'zh-CN',
        autoRefresh,
      })
    );
  };

  const handleAutoRefreshChange = (checked: boolean) => {
    dispatch(
      setDisplaySettings({
        theme,
        refreshInterval: 30000,
        language: 'zh-CN',
        autoRefresh: checked,
      })
    );
  };

  const baseUserMenuItems = [
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: '系统设置',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
  ];

  const userInfoItems = user
    ? [
        {
          key: 'userinfo',
          label: (
            <div style={{ padding: isMobile ? '4px 8px' : '4px 12px', fontWeight: 500 }}>
              {user.username}
            </div>
          ),
          disabled: true as const,
        },
        { type: 'divider' as const },
      ]
    : [];

  const userMenuItems = isMobile
    ? [
        ...userInfoItems,
        {
          key: 'theme',
          label: (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
              }}
              onClick={(event) => event.stopPropagation()}
            >
              <span style={{ fontSize: 14, whiteSpace: 'nowrap' }}>深色模式</span>
              <Switch checked={theme === 'dark'} onChange={handleThemeChange} size="small" />
            </div>
          ),
        },
        {
          key: 'autoRefresh',
          label: (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
              }}
              onClick={(event) => event.stopPropagation()}
            >
              <span style={{ fontSize: 14, whiteSpace: 'nowrap' }}>自动刷新</span>
              <Switch checked={autoRefresh} onChange={handleAutoRefreshChange} size="small" />
            </div>
          ),
        },
        { type: 'divider' as const },
        ...baseUserMenuItems,
      ]
    : [...userInfoItems, ...baseUserMenuItems];

  const handleUserMenuClick = ({ key }: { key: string }) => {
    if (key === 'theme' || key === 'autoRefresh' || key === 'userinfo') {
      return;
    }

    if (key === 'settings') {
      navigate('/settings');
      if (isMobile) {
        setMobileMenuVisible(false);
      }
      return;
    }

    if (key === 'logout') {
      dispatch(logout());
      if (isMobile) {
        setMobileMenuVisible(false);
      }
      navigate('/login', { replace: true });
    }
  };

  const avatarContent = user?.username ? user.username.slice(0, 1).toUpperCase() : <UserOutlined />;

  return (
    <Layout
      style={{
        minHeight: '100vh',
        background: theme === 'dark' ? '#141414' : '#f0f2f5',
      }}
    >
      {!isMobile && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          style={{
            background: theme === 'dark' ? '#1f1f1f' : '#fff',
            borderRight: theme === 'dark' ? '1px solid #303030' : '1px solid #f0f0f0',
          }}
        >
          <div
            style={{
              height: 32,
              margin: 16,
              background: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
              borderRadius: 6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme === 'dark' ? '#fff' : '#000',
              fontWeight: 'bold',
            }}
          >
            {collapsed ? 'TC' : '任务调度系统'}
          </div>
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            items={menuItems}
            onClick={handleMenuClick}
            style={{ background: 'transparent', border: 'none' }}
          />
        </Sider>
      )}

      {/* 移动端抽屉菜单 */}
      {isMobile && (
        <Drawer
          title="任务调度系统"
          placement="left"
          onClose={() => setMobileMenuVisible(false)}
          open={mobileMenuVisible}
          width={280}
          bodyStyle={{ padding: 0 }}
        >
          <Menu
            theme={theme === 'dark' ? 'dark' : 'light'}
            mode="inline"
            selectedKeys={[location.pathname]}
            items={menuItems}
            onClick={handleMenuClick}
            style={{ border: 'none' }}
          />
        </Drawer>
      )}

      <Layout>
        <Header
          style={{
            padding: isMobile ? '0 8px' : '0 16px',
            background: theme === 'dark' ? '#141414' : '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            color: theme === 'dark' ? '#fff' : 'inherit',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Button
              type="text"
              icon={
                isMobile ? (
                  <MenuUnfoldOutlined />
                ) : collapsed ? (
                  <MenuUnfoldOutlined />
                ) : (
                  <MenuFoldOutlined />
                )
              }
              onClick={() =>
                isMobile ? setMobileMenuVisible(true) : setCollapsed((prev) => !prev)
              }
              style={{
                fontSize: 16,
                width: 64,
                height: 64,
                color: theme === 'dark' ? '#fff' : 'inherit',
              }}
            />
            <span
              style={{
                marginLeft: 16,
                fontSize: isMobile ? 14 : 18,
                fontWeight: 'bold',
                display: isMobile ? 'none' : 'block',
              }}
            >
              分布式任务调度系统管理后台
            </span>
            {isMobile && (
              <span style={{ marginLeft: 8, fontSize: 16, fontWeight: 'bold' }}>
                任务调度系统
              </span>
            )}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? 8 : 16,
              flexWrap: isMobile ? 'wrap' : 'nowrap',
            }}
          >
            {!isMobile && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>深色主题:</span>
                  <Switch
                    checked={theme === 'dark'}
                    onChange={handleThemeChange}
                    size="small"
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>自动刷新:</span>
                  <Switch
                    checked={autoRefresh}
                    onChange={handleAutoRefreshChange}
                    size="small"
                  />
                </div>
              </>
            )}
            <Dropdown
              menu={{
                items: userMenuItems,
                onClick: handleUserMenuClick,
              }}
              placement="bottomRight"
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: 'pointer',
                }}
              >
                <Avatar style={{ backgroundColor: '#1890ff' }}>{avatarContent}</Avatar>
                {!isMobile && user?.username && (
                  <span
                    style={{
                      color: theme === 'dark' ? '#fff' : '#000',
                      fontWeight: 500,
                    }}
                  >
                    {user.username}
                  </span>
                )}
              </div>
            </Dropdown>
          </div>
        </Header>
        <Content
          style={{
            margin: isMobile ? '8px' : '16px',
            padding: isMobile ? '12px' : '24px',
            background: theme === 'dark' ? '#1f1f1f' : '#fff',
            borderRadius: 8,
            minHeight: isMobile ? 'calc(100vh - 80px)' : 'calc(100vh - 112px)',
            color: theme === 'dark' ? '#fff' : 'inherit',
          }}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default AppLayout;
